import { useState } from 'react';
import { sampleReviews, StarRating, PlatformIcon, VerificationIcon, getFontFamily, normalizeReviewData } from './SharedComponents';
import { capitalizePlatformName } from '../../utils/platformNames';

// Slider Preview
export const SliderPreview = ({ reviews = [], previewMode = 'desktop', selectedPlatforms = [], widgetConfig = {} }) => {
    const [activeTab, setActiveTab] = useState('all');
    const [currentIndex, setCurrentIndex] = useState(0);

    const displayReviews = (reviews.length > 0 && selectedPlatforms.length > 0)
        ? reviews.slice(0, 8)
        : sampleReviews.slice(0, 8);

    const nextSlide = () => {
        setCurrentIndex((prev) => (prev + 1) % filteredReviews.length);
    };

    const prevSlide = () => {
        setCurrentIndex((prev) => (prev - 1 + filteredReviews.length) % filteredReviews.length);
    };

    // Create platform data from reviews
    const createPlatformData = () => {
        const platformStats = {};
        let totalRating = 0;
        let totalCount = 0;

        displayReviews.forEach(review => {
            const normalized = normalizeReviewData(review);
            const platformName = normalized.platform;

            if (!platformStats[platformName]) {
                platformStats[platformName] = { ratings: [], count: 0 };
            }

            platformStats[platformName].ratings.push(normalized.rating);
            platformStats[platformName].count++;
            totalRating += normalized.rating;
            totalCount++;
        });

        const platforms = [
            {
                id: 'all',
                name: 'All Reviews',
                rating: totalCount > 0 ? (totalRating / totalCount) : 0,
                count: totalCount
            }
        ];

        Object.entries(platformStats).forEach(([platform, stats]) => {
            const avgRating = stats.ratings.reduce((sum, rating) => sum + rating, 0) / stats.ratings.length;
            platforms.push({
                id: platform,
                name: platform.charAt(0).toUpperCase() + platform.slice(1),
                rating: avgRating,
                count: stats.count
            });
        });

        return platforms;
    };

    const platformData = createPlatformData();

    // Filter reviews based on active tab
    const filteredReviews = activeTab === 'all'
        ? displayReviews
        : displayReviews.filter(review => {
            const normalized = normalizeReviewData(review);
            return normalized.platform.toLowerCase() === activeTab.toLowerCase();
        });

    const currentReview = normalizeReviewData(filteredReviews[currentIndex] || filteredReviews[0]);


    // Generate container styles based on widgetConfig
    const getContainerStyles = () => {
        const backgroundConfig = widgetConfig.style?.background || {};
        const width = backgroundConfig.width || 1200;
        const borderRadius = backgroundConfig.borderRadius ?? 16;
        const shadow = backgroundConfig.shadow ?? true;
        const colorScheme = widgetConfig.style?.colorScheme || 'light';

        return {
            width: `${width}px`,
            maxWidth: '100%',
            borderRadius: `${borderRadius}px`,
            boxShadow: shadow ? '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)' : 'none',
            backgroundColor: backgroundConfig.color || (colorScheme === 'dark' ? '#1F2937' : '#FFFFFF'),
            fontFamily: getFontFamily(widgetConfig.style?.fontFamily || 'default')
        };
    };

    // Get style configuration values
    const styleConfig = widgetConfig?.style || {};
    const textSize = styleConfig?.review?.textSize || 16;
    const outlineColor = styleConfig?.review?.outlineColor || '#E5E5E5';
    const textColor = styleConfig?.review?.textColor || '#000000';
    const reviewerNameColor = styleConfig?.review?.reviewerNameColor || '#000000';
    const dateColor = styleConfig?.review?.dateColor || '#666666';

    // Widget configuration
    const widgetTitleConfig = widgetConfig?.widgetTitleConfig || { showTitle: true, title: 'Customer Reviews', caption: 'See what our customers are saying' };
    const headerConfig = widgetConfig?.headerConfig || { showHeader: true, elements: { rating: true, numberOfReviews: true, writeReviewButton: true, heading: true } };
    const tabsConfig = widgetConfig?.tabsConfig || { showTabs: true, elements: { icon: true, name: true, rating: true } };

    const overallRating = platformData.find(p => p.id === 'all');
    const colorScheme = widgetConfig.style?.colorScheme || 'light';

    return (
        <div
            className={`mx-auto ${colorScheme === 'dark' ? 'bg-gray-800' : 'bg-white'}`}
            style={getContainerStyles()}
        >
            {/* Widget Title Section */}
            {widgetTitleConfig.showTitle && (
                <div className={`${previewMode === 'mobile' ? 'mb-4 p-4' : 'mb-6 p-6'}`}>
                    <h1
                        className={`${previewMode === 'mobile' ? 'mb-2' : 'mb-3'}`}
                        style={{
                            color: colorScheme === 'dark' ? '#FFFFFF' : '#000000',
                            fontSize: `${styleConfig?.widgetTitle?.titleSize || 24}px`,
                            fontWeight: styleConfig?.widgetTitle?.titleBold ? '700' : 'normal',
                            fontStyle: styleConfig?.widgetTitle?.titleItalic ? 'italic' : 'normal',
                            textAlign: styleConfig?.widgetTitle?.alignment || 'center'
                        }}
                    >
                        {widgetTitleConfig.title}
                    </h1>
                    {widgetTitleConfig.caption && (
                        <p
                            style={{
                                color: colorScheme === 'dark' ? '#D1D5DB' : '#666666',
                                fontSize: `${styleConfig?.widgetTitle?.captionSize || 15}px`,
                                fontWeight: styleConfig?.widgetTitle?.captionBold ? '700' : 'normal',
                                fontStyle: styleConfig?.widgetTitle?.captionItalic ? 'italic' : 'normal',
                                textAlign: styleConfig?.widgetTitle?.alignment || 'center'
                            }}
                        >
                            {widgetTitleConfig.caption}
                        </p>
                    )}
                </div>
            )}

            {/* Header Section */}
            {headerConfig.showHeader && (
                <div className={`text-center ${previewMode === 'mobile' ? 'mb-6 px-4' : 'mb-8 px-6'}`}>
                    {/* Platform Tabs */}
                    {tabsConfig.showTabs && (
                        <div className={`flex flex-wrap justify-center gap-2 ${previewMode === 'mobile' ? 'mb-4' : 'mb-6'}`}>
                            {platformData.slice(0, previewMode === 'mobile' ? 3 : platformData.length).map((platform) => (
                                <button
                                    key={platform.id}
                                    onClick={() => {
                                        setActiveTab(platform.id);
                                        setCurrentIndex(0); // Reset to first review when changing tabs
                                    }}
                                    className={`flex items-center space-x-2 rounded-md border transition-all duration-200 ${previewMode === 'mobile' ? 'px-3 py-2' : 'px-5 py-3'} ${activeTab === platform.id
                                        ? 'bg-gray-900 text-white border-gray-900'
                                        : 'bg-white text-gray-700 border-gray-300 hover:border-gray-400 hover:bg-gray-50'
                                        }`}
                                >
                                    {platform.id !== 'all' && tabsConfig.elements.icon && (
                                        <PlatformIcon
                                            platform={platform.id}
                                            className={previewMode === 'mobile' ? 'w-4 h-4' : 'w-5 h-5'}
                                        />
                                    )}
                                    {tabsConfig.elements.name && (
                                        <span className={`font-medium ${previewMode === 'mobile' ? 'text-sm' : ''}`}>{capitalizePlatformName(platform.name)}</span>
                                    )}
                                    {tabsConfig.elements.rating && (
                                        <span className={`font-bold ${previewMode === 'mobile' ? 'text-sm' : ''}`}>{platform.rating.toFixed(1)}</span>
                                    )}
                                </button>
                            ))}
                        </div>
                    )}

                    {/* Overall Rating Section */}
                    {(headerConfig.elements.rating || headerConfig.elements.numberOfReviews || headerConfig.elements.writeReviewButton) && (
                        <div className={`mx-auto bg-gray-50 rounded-lg ${previewMode === 'mobile' ? 'max-w-sm p-4 mb-6 space-y-3' : 'max-w-2xl p-6 mb-8 flex items-center justify-between'}`}>
                            {(headerConfig.elements.rating || headerConfig.elements.numberOfReviews) && (
                                <div className={`${previewMode === 'mobile' ? 'text-center' : 'flex items-center space-x-4'}`}>
                                    <div>
                                        <div className={previewMode === 'mobile' ? 'text-center' : 'text-left'}>
                                            {headerConfig.elements.heading && (
                                                <div className={`font-medium text-gray-600 mb-1 ${previewMode === 'mobile' ? 'text-xs' : 'text-sm'}`}>Overall Rating</div>
                                            )}
                                            <div className={`flex items-center ${previewMode === 'mobile' ? 'justify-center space-x-2' : 'space-x-3'}`}>
                                                {headerConfig.elements.rating && (
                                                    <>
                                                        <span className={`font-bold text-gray-900 ${previewMode === 'mobile' ? 'text-2xl' : 'text-3xl'}`}>{overallRating.rating.toFixed(1)}</span>
                                                        <div className="flex space-x-1">
                                                            {[1, 2, 3, 4, 5].map((star) => (
                                                                <svg
                                                                    key={star}
                                                                    className={`${previewMode === 'mobile' ? 'w-4 h-4' : 'w-6 h-6'} fill-current`}
                                                                    style={{
                                                                        color: star <= overallRating.rating
                                                                            ? (widgetConfig.style?.rating?.starsColor || '#F1C40F')
                                                                            : '#d1d5db'
                                                                    }}
                                                                    viewBox="0 0 24 24"
                                                                >
                                                                    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                                                                </svg>
                                                            ))}
                                                        </div>
                                                    </>
                                                )}
                                                {headerConfig.elements.numberOfReviews && (
                                                    <span className={`text-gray-500 ${previewMode === 'mobile' ? 'text-sm' : ''}`}>({overallRating.count.toLocaleString()})</span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                            {headerConfig.elements.writeReviewButton && (
                                <button
                                    className={`text-white font-medium rounded-lg transition-colors duration-200 ${previewMode === 'mobile' ? 'px-4 py-2 text-sm w-full' : 'px-6 py-2'}`}
                                    style={{ backgroundColor: styleConfig?.accentColor || '#4A90E2' }}
                                >
                                    Write a review
                                </button>
                            )}
                        </div>
                    )}
                </div>
            )}
            {/* Slider Layout */}
            <div className={`${previewMode === 'mobile' ? 'p-4' : 'px-6 pb-6'}`}>
                <div className="relative">
                    {/* Navigation Arrows */}
                    <button
                        onClick={prevSlide}
                        className="absolute left-0 top-1/2 transform -translate-y-1/2 z-10 p-2 rounded-full bg-white shadow-md hover:bg-gray-50 border transition-colors duration-200"
                        disabled={filteredReviews.length <= 1}
                    >
                        <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                    </button>

                    <button
                        onClick={nextSlide}
                        className="absolute right-0 top-1/2 transform -translate-y-1/2 z-10 p-2 rounded-full bg-white shadow-md hover:bg-gray-50 border transition-colors duration-200"
                        disabled={filteredReviews.length <= 1}
                    >
                        <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                    </button>

                    {/* Review Card */}
                    <div className="mx-12 min-h-[140px] flex items-center">
                        <div className="w-full">
                            <div className="border-b pb-3" style={{ borderColor: outlineColor }}>
                                <div className="flex items-start space-x-3">
                                    <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center text-white font-semibold text-sm flex-shrink-0">
                                        {currentReview?.avatar}
                                    </div>
                                    <div className="flex-1">
                                        <div className="flex items-center space-x-2 mb-2">
                                            <div className="flex items-center space-x-1">
                                                <h4 className="font-semibold text-sm" style={{ color: reviewerNameColor }}>{currentReview?.name}</h4>
                                                {currentReview?.isVerified && (
                                                    <VerificationIcon
                                                        size="12"
                                                        accentColor={widgetConfig.style?.accentColor || '#4A90E2'}
                                                    />
                                                )}
                                            </div>
                                            <StarRating
                                                rating={currentReview?.rating}
                                                size="w-4 h-4"
                                                color={widgetConfig.style?.rating?.starsColor || '#F1C40F'}
                                            />
                                            <PlatformIcon platform={currentReview?.platform} className="w-4 h-4" />
                                        </div>
                                        <p className="leading-relaxed" style={{ color: textColor, fontSize: `${textSize}px` }}>{currentReview?.comment}</p>
                                        <p className="text-xs mt-2" style={{ color: dateColor }}>{currentReview?.date}</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Dot Indicators */}
                    {filteredReviews.length > 1 && (
                        <div className="flex justify-center mt-4 space-x-2">
                            {filteredReviews.map((_, index) => (
                                <button
                                    key={index}
                                    onClick={() => setCurrentIndex(index)}
                                    className="h-2 rounded-full transition-all duration-200"
                                    style={{
                                        width: index === currentIndex ? '24px' : '8px',
                                        backgroundColor: index === currentIndex
                                            ? (styleConfig.pagination?.color || '#4A90E2')
                                            : '#D1D5DB'
                                    }}
                                    onMouseOver={(e) => {
                                        if (index !== currentIndex) {
                                            e.target.style.backgroundColor = '#9CA3AF';
                                        }
                                    }}
                                    onMouseOut={(e) => {
                                        if (index !== currentIndex) {
                                            e.target.style.backgroundColor = '#D1D5DB';
                                        }
                                    }}
                                />
                            ))}
                        </div>
                    )}

                    {/* Review Counter */}
                    <div className="text-center mt-2 text-xs text-gray-500">
                        {currentIndex + 1} of {filteredReviews.length} reviews
                    </div>
                </div>
            </div>
        </div>
    );
}; 